import  { Chain, Quote, userAgent, Response, ChainID, Source } from "./CovalentClient";
import { debugOutput } from "../util/ApiHelpers";
import { parseISO } from "date-fns";
import { ExponentialBackoff } from "../util/backoff";
import pLimit from "p-limit";
import { Chains } from "../util/Chains";
import { ApiKeyValidator } from "../util/ApiKeyValidator";

class ApprovalsResponse {
    /** * The requested address. */
    address: string;
    /** * The timestamp when the response was generated. Useful to show data staleness to users. */
    updated_at: Date;
    /** * The requested quote currency eg: `USD`. */
    quote_currency: string;
    /** * The requested chain ID eg: `1`. */
    chain_id: number;
    /** * The requested chain name eg: `eth-mainnet`. */
    chain_name: string;
    /** * List of response items. */
    items: TokensApprovalItem[];

    constructor(data: ApprovalsResponse) {
        this.address = data.address;
        this.updated_at = data.updated_at && data.updated_at !== null ? parseISO(data.updated_at.toString()) : null;
        this.quote_currency = data.quote_currency;
        this.chain_id = data.chain_id;
        this.chain_name = data.chain_name;
        this.items = data.items && data.items !== null ? data.items.map((itemData: TokensApprovalItem) => new TokensApprovalItem(itemData)) : null;
    }
}
class TokensApprovalItem {
    /** * The address for the token that has approvals. */
    token_address: string;
    /** * The name for the token that has approvals. */
    token_address_label: string;
    /** * The ticker symbol for this contract. This field is set by a developer and non-unique across a network. */
    ticker_symbol: string;
    /** * Use contract decimals to format the token balance for display purposes - divide the balance by `10^{contract_decimals}`. */
    contract_decimals: number;
    /** * The contract logo URL. */
    logo_url: string;
    /** * The exchange rate for the requested quote currency. */
    quote_rate: number;
    /** * Wallet balance of the token. */
    balance: bigint | null;
    /** * Value of the wallet balance of the token. */
    balance_quote: number;
    /** * A prettier version of the quote for rendering purposes. */
    pretty_balance_quote: string;
    /** * Total amount at risk across all spenders. */
    value_at_risk: string;
    /** * Value of total amount at risk across all spenders. */
    value_at_risk_quote: number;
    /** * A prettier version of the quote for rendering purposes. */
    pretty_value_at_risk_quote: string;
    /** * Contracts with non-zero approvals for this token. */
    spenders: TokenSpenderItem[];

    constructor(data: TokensApprovalItem) {
        this.token_address = data.token_address;
        this.token_address_label = data.token_address_label;
        this.ticker_symbol = data.ticker_symbol;
        this.contract_decimals = data.contract_decimals;
        this.logo_url = data.logo_url;
        this.quote_rate = data.quote_rate;
        this.balance = data.balance && data.balance !== null ? BigInt(data.balance) : null;
        this.balance_quote = data.balance_quote;
        this.pretty_balance_quote = data.pretty_balance_quote;
        this.value_at_risk = data.value_at_risk;
        this.value_at_risk_quote = data.value_at_risk_quote;
        this.pretty_value_at_risk_quote = data.pretty_value_at_risk_quote;
        this.spenders = data.spenders && data.spenders !== null ? data.spenders.map((itemData: TokenSpenderItem) => new TokenSpenderItem(itemData)) : null;
    }
}
class TokenSpenderItem {
    /** * The height of the block. */
    block_height: number;
    /** * The offset is the position of the tx in the block. */
    tx_offset: number;
    /** * The offset is the position of the log entry within an event log." */
    log_offset: number;
    /** * The block signed timestamp in UTC. */
    block_signed_at: Date;
    /** * Most recent transaction that updated approval amounts for the token. */
    tx_hash: string;
    /** * Address of the contract with approval for the token. */
    spender_address: string;
    /** * Name of the contract with approval for the token. */
    spender_address_label: string;
    /** * Remaining number of tokens granted to the spender by the approval. */
    allowance: string;
    /** * Value of the remaining allowance specified by the approval. */
    allowance_quote: number;
    /** * A prettier version of the quote for rendering purposes. */
    pretty_allowance_quote: string;
    /** * Amount at risk for spender. */
    value_at_risk: string;
    /** * Value of amount at risk for spender. */
    value_at_risk_quote: number;
    /** * A prettier version of the quote for rendering purposes. */
    pretty_value_at_risk_quote: string;
    risk_factor: string;

    constructor(data: TokenSpenderItem) {
        this.block_height = data.block_height;
        this.tx_offset = data.tx_offset;
        this.log_offset = data.log_offset;
        this.block_signed_at = data.block_signed_at && data.block_signed_at !== null ? parseISO(data.block_signed_at.toString()) : null;
        this.tx_hash = data.tx_hash;
        this.spender_address = data.spender_address;
        this.spender_address_label = data.spender_address_label;
        this.allowance = data.allowance;
        this.allowance_quote = data.allowance_quote;
        this.pretty_allowance_quote = data.pretty_allowance_quote;
        this.value_at_risk = data.value_at_risk;
        this.value_at_risk_quote = data.value_at_risk_quote;
        this.pretty_value_at_risk_quote = data.pretty_value_at_risk_quote;
        this.risk_factor = data.risk_factor;
            
    }
}

class NftApprovalsResponse {
    /** * The timestamp when the response was generated. Useful to show data staleness to users. */
    updated_at: Date;
    /** * The requested chain ID eg: `1`. */
    chain_id: number;
    /** * The requested chain name eg: `eth-mainnet`. */
    chain_name: string;
    /** * The requested address. */
    address: string;
    /** * List of response items. */
    items: NftApprovalsItem[];

    constructor(data: NftApprovalsResponse) {
        this.updated_at = data.updated_at && data.updated_at !== null ? parseISO(data.updated_at.toString()) : null;
        this.chain_id = data.chain_id;
        this.chain_name = data.chain_name;
        this.address = data.address;
        this.items = data.items && data.items !== null ? data.items.map((itemData: NftApprovalsItem) => new NftApprovalsItem(itemData)) : null;
    }
}
class NftApprovalsItem {
    /** * Use the relevant `contract_address` to lookup prices, logos, token transfers, etc. */
    contract_address: string;
    /** * The label of the contract address. */
    contract_address_label: string;
    /** * The ticker symbol for this contract. This field is set by a developer and non-unique across a network. */
    contract_ticker_symbol: string;
    /** * List of asset balances held by the user. */
    token_balances: NftApprovalBalance[];
    /** * Contracts with non-zero approvals for this token. */
    spenders: NftApprovalSpender[];

    constructor(data: NftApprovalsItem) {
        this.contract_address = data.contract_address;
        this.contract_address_label = data.contract_address_label;
        this.contract_ticker_symbol = data.contract_ticker_symbol;
        this.token_balances = data.token_balances && data.token_balances !== null ? data.token_balances.map((itemData: NftApprovalBalance) => new NftApprovalBalance(itemData)) : null;
        this.spenders = data.spenders && data.spenders !== null ? data.spenders.map((itemData: NftApprovalSpender) => new NftApprovalSpender(itemData)) : null;
    }
}
class NftApprovalBalance {
    /** * The token's id. */
    token_id: bigint | null;
    /** * The NFT's token balance. */
    token_balance: bigint | null;

    constructor(data: NftApprovalBalance) {
        this.token_id = data.token_id && data.token_id !== null ? BigInt(data.token_id) : null;
        this.token_balance = data.token_balance && data.token_balance !== null ? BigInt(data.token_balance) : null;
            
    }
}
class NftApprovalSpender {
    /** * The height of the block. */
    block_height: number;
    /** * The offset is the position of the tx in the block. */
    tx_offset: number;
    /** * The offset is the position of the log entry within an event log." */
    log_offset: number;
    /** * The block signed timestamp in UTC. */
    block_signed_at: Date;
    /** * Most recent transaction that updated approval amounts for the token. */
    tx_hash: string;
    /** * Address of the contract with approval for the token. */
    spender_address: string;
    /** * Name of the contract with approval for the token. */
    spender_address_label: string;
    /** * The token ids approved. */
    token_ids_approved: string;
    /** * Remaining number of tokens granted to the spender by the approval. */
    allowance: string;

    constructor(data: NftApprovalSpender) {
        this.block_height = data.block_height;
        this.tx_offset = data.tx_offset;
        this.log_offset = data.log_offset;
        this.block_signed_at = data.block_signed_at && data.block_signed_at !== null ? parseISO(data.block_signed_at.toString()) : null;
        this.tx_hash = data.tx_hash;
        this.spender_address = data.spender_address;
        this.spender_address_label = data.spender_address_label;
        this.token_ids_approved = data.token_ids_approved;
        this.allowance = data.allowance;
            
    }
}

/**
 * Approvals API
 * 
 */

export class SecurityService {
    private LIMIT;
    constructor(private apiKey: string, private debug: boolean = false, private threadCount: number = 3, private is_key_valid: boolean, private enableRetry: boolean = true, private source: Source) {
        this.LIMIT = pLimit(this.threadCount);
    }
    


    /**
     *
     * Commonly used to get a list of approvals across all token contracts categorized by spenders for a wallet’s assets.
     * 
     * @param {string} chainName - The chain name eg: `eth-mainnet`.
     * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     *
     */
    public async getApprovals(chainName: Chain | Chains | ChainID, walletAddress: string): Promise<Response<ApprovalsResponse>> {
        let success = false;
        let data: Response<ApprovalsResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<ApprovalsResponse>>(this.apiKey, this.debug);
        while (!success) {
            try {
                const urlParams = new URLSearchParams();

                const url = `https://api.covalenthq.com/v1/${chainName}/approvals/${walletAddress}/?${urlParams}`;

                if (!this.is_key_valid) {
                    return {
                        data: null,
                        error: true,
                        error_code: 401,
                        error_message: ApiKeyValidator.INVALID_API_KEY_MESSAGE
                    };
                }

                let startTime;
                if (this.debug) {
                    startTime = new Date();
                }

                response = await this.LIMIT(() => fetch(`https://api.covalenthq.com/v1/${chainName}/approvals/${walletAddress}/?${urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this.apiKey}`,
                        "X-Requested-With": this.source ? this.source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));
                
                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this.enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await this.LIMIT(() => backoff.backOff(response ? response.url : url, this.source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                const dataClass = new ApprovalsResponse(data.data);
                
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };

            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }

    /**
     *
     * @param {string} chainName - The chain name eg: `eth-mainnet`.
     * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     
     *
     */
    public async getNftApprovals(chainName: Chain | Chains | ChainID, walletAddress: string): Promise<Response<NftApprovalsResponse>> {
        let success = false;
        let data: Response<NftApprovalsResponse>;
        let response: globalThis.Response;
        const backoff = new ExponentialBackoff<Response<NftApprovalsResponse>>(this.apiKey, this.debug);
        while (!success) {
            try {
                const urlParams = new URLSearchParams();

                const url = `https://api.covalenthq.com/v1/${chainName}/nft/approvals/${walletAddress}/?${urlParams}`;

                if (!this.is_key_valid) {
                    return {
                        data: null,
                        error: true,
                        error_code: 401,
                        error_message: ApiKeyValidator.INVALID_API_KEY_MESSAGE
                    };
                }

                let startTime;
                if (this.debug) {
                    startTime = new Date();
                }
                
                response = await this.LIMIT(() => fetch(`https://api.covalenthq.com/v1/${chainName}/nft/approvals/${walletAddress}/?${urlParams}`, {
                    headers: {
                        "Authorization": `Bearer ${this.apiKey}`,
                        "X-Requested-With": this.source ? this.source + " " + "(" + userAgent + ")" : userAgent
                    }
                }));

                debugOutput(response ? response.url : url, response ? response.status : 429, startTime);

                if ((this.enableRetry === true) && (!response || response.status === null || response.status === 429)) {
                    try {
                        data = await this.LIMIT(() => backoff.backOff(response ? response.url : url, this.source));
                    } catch (error) {
                        success = true;
                        return {
                            data: null,
                            error: true,
                            error_code: response.status ?? 429,
                            error_message: error.message
                        };
                    }
                } else {
                    data = await response.json();
                }
                
                const dataClass = new NftApprovalsResponse(data.data);
                
                success = true;
                return {
                    data: dataClass,
                    error: data ? data.error : true,
                    error_code: data ? data.error_code : response.status,
                    error_message: data ? data.error_message : response.status === 500 ? "Internal server error" : response.status === 429 ? "429 Timeout Error" : "Unknown Error"
                };
            } catch (error) {
                success = true;
                return {
                    data: null,
                    error: true,
                    error_code: error.cause ? error.cause.code : data ? data.error_code : response ? response.status : "Unknown Error Code",
                    error_message: error.cause ? error.cause.message : data ? data.error_message : response && response.status === 500 ? "Internal server error" : !response ? "Unknown Error" : "401 Authorization Required"
                };
            }
        }
    }    

}
    
    